import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MarketingServiceAgreementPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Marketing Services Agreement
          </h1>
          <p className="text-lg text-gray-600">
            Testimonial License and Compensation — Asha Health, Inc.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="prose prose-lg max-w-none">

              {/* Acceptance Box */}
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-12">
                <p className="font-bold text-gray-900 mb-2">
                  BY CHECKING THE ACCEPTANCE BOX ON THE TESTIMONIAL SUBMISSION FORM, YOU:
                </p>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>Acknowledge that you have read, understood, and agree to be bound by this Agreement</li>
                  <li>Represent and warrant that you have the authority to bind yourself and/or your organization to this Agreement</li>
                  <li>Confirm that you are a current or former customer of Asha Health, Inc.</li>
                </ol>
              </div>

              {/* Preamble */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4">
                  This Marketing Services Agreement ("Agreement") is entered into as of the date of electronic acceptance ("Effective Date") by and between:
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong className="text-gray-900">Asha Health, Inc.</strong>, a Delaware corporation ("Company"); and
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The individual or entity accepting this Agreement via the testimonial submission form ("Provider"),
                </p>
                <p className="text-gray-700 leading-relaxed">
                  collectively referred to as the "Parties."
                </p>
              </div>

              {/* Recitals */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">RECITALS</h2>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">WHEREAS,</strong> Company develops and markets dental practice management software and artificial intelligence solutions for dental practices; and
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">WHEREAS,</strong> Provider is a current or former customer of Company and is willing to provide a testimonial regarding Provider's experience with Company's products and services, subject to the terms set forth herein.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">NOW, THEREFORE,</strong> in consideration of the mutual covenants herein, the Parties agree as follows:
                  </p>
                </div>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">1. SERVICES</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">1.1 Testimonial.</strong> Provider agrees to participate in the creation of a testimonial ("Testimonial") regarding Provider's experience with Company's products and services. The Testimonial may take the form of a written statement, video recording, audio recording, or any combination thereof, as reasonably directed by Company.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">1.2 Re-Recording.</strong> Company may, at its sole discretion, request that Provider re-record, revise, or update the Testimonial at any time during the term of this Agreement. Provider agrees to make commercially reasonable efforts to accommodate such requests. Any re-recorded or revised Testimonial shall be subject to the same terms as the original Testimonial under this Agreement.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">1.3 Truthfulness.</strong> Provider represents and warrants that all statements made in the Testimonial are truthful, accurate, and based on Provider's genuine experience with Company's products and services. Provider shall not make any claims that Provider cannot substantiate.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">1.4 Cooperation.</strong> Provider agrees to cooperate with Company in good faith in the production of the Testimonial, including making themselves available at mutually agreed-upon times for recording sessions.
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">2. COMPENSATION</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">2.1 Payment.</strong> Subject to Company's acceptance of the Testimonial pursuant to Section 2.2, Company shall pay Provider a one-time fee of $500.00 ("Testimonial Fee").
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">2.2 Acceptance.</strong> Payment of the Testimonial Fee is contingent upon Company's acceptance of the Testimonial in its sole discretion. Company reserves the right to reject any Testimonial for any reason, including but not limited to quality, content, or messaging concerns. Provider shall have no right to the Testimonial Fee unless and until Company provides written confirmation of acceptance.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">2.3 Payment Terms.</strong> The Testimonial Fee shall be payable within thirty (30) days of Company's written acceptance of the Testimonial via ACH transfer or check.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">2.4 Taxes.</strong> Provider is solely responsible for all taxes arising from compensation received under this Agreement. Company shall issue an IRS Form 1099 to Provider if required by applicable law.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">2.5 No Additional Compensation.</strong> Provider acknowledges that no additional compensation is owed for re-recordings, revisions, or updates requested under Section 1.2, unless separately agreed to in writing by the Parties.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">3. LICENSE AND INTELLECTUAL PROPERTY</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">3.1 License Grant.</strong> Upon acceptance of the Testimonial, Provider grants to Company a perpetual, irrevocable, worldwide, royalty-free, fully transferable, sublicensable license to use, reproduce, modify, distribute, display, perform, and create derivative works of the Testimonial, Provider's name, likeness, voice, photograph, and professional title in any and all media now known or hereafter developed, including but not limited to Company's website, social media channels, marketing materials, sales collateral, presentations, advertisements, and trade show displays.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">3.2 Editing Rights.</strong> Company may edit, crop, excerpt, or otherwise modify the Testimonial for length, clarity, or format, provided that such modifications do not materially alter the substance or meaning of Provider's statements.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">3.3 No Obligation to Use.</strong> Company is under no obligation to use the Testimonial and may cease using the Testimonial at any time without notice to Provider.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">3.4 Ownership.</strong> All right, title, and interest in and to any materials created by Company incorporating the Testimonial shall be and remain the sole property of Company.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">4. FTC COMPLIANCE AND DISCLOSURE</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">4.1 Material Connection.</strong> Provider acknowledges that the compensation paid under this Agreement constitutes a material connection between Provider and Company under the Federal Trade Commission's ("FTC") Endorsement Guides (16 CFR Part 255). Provider agrees to clearly and conspicuously disclose this material connection whenever Provider endorses Company's products or services in any medium, whether or not the endorsement uses the Testimonial created under this Agreement.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">4.2 Company Disclosure.</strong> Company shall include appropriate disclosures regarding the compensated nature of the Testimonial in connection with its use of the Testimonial, in accordance with applicable FTC guidelines.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">5. CONFIDENTIALITY AND HIPAA</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">5.1 No PHI.</strong> Provider shall not include any Protected Health Information ("PHI") as defined under the Health Insurance Portability and Accountability Act ("HIPAA") of any patient in the Testimonial. Provider is solely responsible for ensuring compliance with HIPAA and all applicable privacy laws in connection with Provider's participation under this Agreement.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">5.2 Confidentiality.</strong> Each Party agrees to keep confidential the terms of this Agreement, including the Testimonial Fee, except as required by law or as necessary to enforce the terms hereof. Notwithstanding the foregoing, Company may disclose the compensated nature of the Testimonial as required by Section 4.
                  </p>
                </div>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">6. REPRESENTATIONS AND WARRANTIES</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">6.1 Provider Representations.</strong> Provider represents and warrants that: (a) Provider has the full right, power, and authority to enter into this Agreement and grant the rights herein; (b) the Testimonial does not and will not infringe upon or violate the rights of any third party; (c) Provider is not under any obligation or restriction that would prevent or interfere with Provider's performance under this Agreement; and (d) Provider is or was a bona fide customer of Company at the time of providing the Testimonial.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">6.2 No Guarantees.</strong> Provider shall not make any claims in the Testimonial that guarantee specific results, outcomes, or performance metrics from using Company's products or services unless such claims can be independently verified and substantiated.
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">7. INDEMNIFICATION</h3>
                <p className="text-gray-700 leading-relaxed">
                  Provider agrees to indemnify, defend, and hold harmless Company and its officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) any breach of this Agreement by Provider; (b) any false, misleading, or unsubstantiated statement made by Provider in the Testimonial; or (c) any violation of applicable law by Provider in connection with this Agreement.
                </p>
              </div>

              {/* Section 8 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">8. TERM AND TERMINATION</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">8.1 Term.</strong> This Agreement shall commence on the Effective Date and shall remain in effect indefinitely unless terminated pursuant to Section 8.2.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">8.2 Termination.</strong> Either Party may terminate this Agreement upon thirty (30) days' written notice to the other Party. Notwithstanding any such termination, the license granted in Section 3 shall survive termination and remain in full force and effect in perpetuity.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">8.3 Survival.</strong> Sections 3 (License and Intellectual Property), 4 (FTC Compliance), 5 (Confidentiality), 6 (Representations and Warranties), 7 (Indemnification), and this Section 8.3 shall survive any termination or expiration of this Agreement.
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">9. GENERAL PROVISIONS</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.1 Independent Contractor.</strong> Provider is an independent contractor and nothing in this Agreement shall be construed to create a partnership, joint venture, agency, or employment relationship between the Parties.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.2 Entire Agreement.</strong> This Agreement constitutes the entire agreement between the Parties with respect to the subject matter hereof and supersedes all prior negotiations, representations, and agreements.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.3 Amendment.</strong> This Agreement may not be amended or modified except by a written instrument signed by both Parties.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.4 Governing Law.</strong> This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of laws principles.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.5 Severability.</strong> If any provision of this Agreement is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.6 Notices.</strong> All notices under this Agreement shall be in writing and delivered via email with confirmation of receipt to the email addresses provided at the time of acceptance, or via nationally recognized overnight courier.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.7 Assignment.</strong> Provider may not assign this Agreement or any rights hereunder without the prior written consent of Company. Company may freely assign this Agreement.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">9.8 Waiver.</strong> No waiver of any provision of this Agreement shall be effective unless in writing and signed by the waiving Party. No failure to exercise any right shall constitute a waiver thereof.
                  </p>
                </div>
              </div>

              {/* Electronic Acceptance */}
              <div className="mb-8">
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">ELECTRONIC ACCEPTANCE.</strong> By checking the acceptance box and submitting the testimonial form, Provider agrees to be bound by the terms of this Agreement. Electronic acceptance constitutes a valid and binding signature for all purposes under this Agreement. The date and time of form submission shall serve as the Effective Date.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketingServiceAgreementPage;
